import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Mail, Plus, Copy, X, Shield, UserCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const roleStyle = {
  owner: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  admin: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  member: "bg-slate-700 text-slate-300 border-slate-600",
};

export default function Team() {
  const { role, org, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "member" });
  const canInvite = role === "owner" || role === "admin";
  const RANK = { member: 1, admin: 2, owner: 3 };
  const canRemove = (m) => canInvite && m.user_id !== user?.id && m.role !== "owner" && RANK[m.role] < RANK[role];

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.full_name} from ${org?.name}? They lose access immediately.`)) return;
    try {
      await api.delete(`/org/members/${m.user_id}`);
      setMembers((prev) => prev.filter((x) => x.user_id !== m.user_id));
      toast.success(`${m.full_name} was removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove member");
    }
  };

  const load = async () => {
    const [m, i] = await Promise.all([
      api.get("/org/members").then((r) => r.data),
      canInvite ? api.get("/org/invitations").then((r) => r.data).catch(() => []) : Promise.resolve([]),
    ]);
    setMembers(m);
    setInvites(i);
  };
  useEffect(() => { load(); }, [org?.id, canInvite]);

  const invite = async () => {
    try {
      const { data } = await api.post("/org/invitations", form);
      toast.success("Invite created — share the link");
      setOpen(false);
      setForm({ email: "", role: "member" });
      load();
      const link = `${window.location.origin}/invite/${data.token}`;
      try { await navigator.clipboard.writeText(link); toast.info("Invite link copied to clipboard"); } catch {}
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invite");
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/org/invitations/${id}`);
      toast.success("Invitation revoked");
      load();
    } catch {
      toast.error("Failed to revoke");
    }
  };

  const copyLink = async (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    await navigator.clipboard.writeText(link);
    toast.info("Invite link copied");
  };

  const pending = invites.filter((i) => !i.accepted && !i.revoked);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto">
      <header className="flex flex-wrap justify-between items-end gap-4 mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">Organization</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Team & Access</h1>
          <p className="text-sm text-slate-400 mt-1">Members and pending invitations for <span className="text-white font-semibold">{org?.name}</span></p>
        </div>
        {canInvite && (
          <Button onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold" data-testid="team-invite-btn">
            <Plus className="w-4 h-4 mr-1" /> Invite teammate
          </Button>
        )}
      </header>

      <Card className="bg-slate-900 border-slate-800 mb-4">
        <div className="p-5 border-b border-slate-800 flex items-center gap-2">
          <Users className="w-4 h-4 text-orange-400" />
          <h2 className="font-display text-lg font-semibold">Members ({members.length})</h2>
        </div>
        <div>
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 border-b border-slate-800/50 last:border-0 flex-wrap sm:flex-nowrap" data-testid={`member-${m.user_id}`}>
              <div className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-orange-400">{m.full_name?.[0]?.toUpperCase() || "?"}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{m.full_name}</p>
                <p className="text-xs text-slate-500 font-mono truncate">{m.email}</p>
              </div>
              <Badge className={`text-xs border capitalize ${roleStyle[m.role] || roleStyle.member}`}>{m.role}</Badge>
              {canInvite ? (
                <div className="flex items-center gap-2 ml-auto sm:ml-0" data-testid={`member-bookable-${m.user_id}`}>
                  <UserCheck className={`w-3.5 h-3.5 ${m.bookable !== false ? "text-emerald-400" : "text-slate-600"}`} />
                  <Switch
                    checked={m.bookable !== false}
                    onCheckedChange={async (next) => {
                      try {
                        await api.patch(`/org/members/${m.user_id}`, { bookable: next });
                        setMembers(prev => prev.map(x => x.user_id === m.user_id ? { ...x, bookable: next } : x));
                        toast.success(next ? `${m.full_name} is now bookable for inspections` : `${m.full_name} will no longer appear in the estimator dropdown`);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not update");
                      }
                    }}
                    aria-label="Bookable for estimates"
                    data-testid={`member-bookable-toggle-${m.user_id}`}
                  />
                  <span className="text-xs text-slate-400 whitespace-nowrap hidden sm:inline">Bookable</span>
                </div>
              ) : (
                m.bookable !== false ? (
                  <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[10px]">Bookable</Badge>
                ) : (
                  <Badge className="bg-slate-800 text-slate-500 border-slate-700 text-[10px]">Not bookable</Badge>
                )
              )}
              <span className="text-xs text-slate-500 font-mono hidden lg:block">
                joined {new Date(m.joined_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </span>
              {canRemove(m) && (
                <Button size="sm" variant="ghost" onClick={() => removeMember(m)} className="text-red-300 hover:text-red-200 hover:bg-red-500/10" data-testid={`member-remove-${m.user_id}`}>
                  <X className="w-3.5 h-3.5 mr-1" /> Remove
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      {canInvite && (
      <Card className="bg-slate-900 border-slate-800">
        <div className="p-5 border-b border-slate-800 flex items-center gap-2">
          <Mail className="w-4 h-4 text-orange-400" />
          <h2 className="font-display text-lg font-semibold">Pending invitations ({pending.length})</h2>
        </div>
        {pending.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">No pending invites.</p>
        ) : (
          <div>
            {pending.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 px-5 py-4 border-b border-slate-800/50 last:border-0" data-testid={`invite-${inv.id}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{inv.email}</p>
                  <p className="text-xs text-slate-500 font-mono truncate">
                    invited by {inv.invited_by_email} · expires {new Date(inv.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <Badge className={`text-xs border capitalize ${roleStyle[inv.role] || roleStyle.member}`}>{inv.role}</Badge>
                {canInvite && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => copyLink(inv.token)} data-testid={`invite-copy-${inv.id}`} className="text-slate-300 hover:text-white hover:bg-slate-800">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => revoke(inv.id)} data-testid={`invite-revoke-${inv.id}`} className="text-rose-400 hover:text-rose-300 hover:bg-slate-800">
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
      )}

      {!canInvite && (
        <p className="text-xs text-slate-500 mt-4 flex items-center gap-2">
          <Shield className="w-3 h-3" /> Only owners and admins can invite or revoke access.
        </p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>Invite teammate to {org?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">Send a signed invite link — the recipient joins this organization with the role you choose.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="estimator@apexroofing.com" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="member">Member — full access to leads & pipeline</SelectItem>
                  <SelectItem value="admin">Admin — can also invite others</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">
              We'll copy an invite link to your clipboard — send it to your teammate to join.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={invite} data-testid="invite-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold">Create invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
